<script setup>
// Voice (the assistant's voice prompt, with its history): what the assistant knows and how it speaks on the phone.
// The page composes four parts — where the prompt is in use and whether it drifted, the instruction blocks that can
// be edited, the compiled prompt (text and voice) and the history (versions and the audit log) — and owns the two
// things that need a reason: publishing and restoring a version. Administrators only (the route says so).
// "Save" keeps the edits; "Publish" makes a version of what is saved, so it waits for unsaved edits to be saved.
import { computed, ref, watch } from 'vue';
import { useElementSize } from '@vueuse/core';
import { onBeforeRouteLeave, onBeforeRouteUpdate, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import { Button } from 'dashboard/components-next/polaris/ui/button';
import PageLayout from 'dashboard/components-next/polaris/PageLayout.vue';
import SettingsSection from 'dashboard/components-next/polaris/cards/SettingsSection.vue';
import VoiceCompiledPrompt from 'dashboard/components-next/polaris/pageComponents/voice/VoiceCompiledPrompt.vue';
import VoiceHistory from 'dashboard/components-next/polaris/pageComponents/voice/VoiceHistory.vue';
import VoiceInstructionsForm from 'dashboard/components-next/polaris/pageComponents/voice/VoiceInstructionsForm.vue';
import VoicePublicationStatus from 'dashboard/components-next/polaris/pageComponents/voice/VoicePublicationStatus.vue';
import VoiceReasonDialog from 'dashboard/components-next/polaris/pageComponents/voice/VoiceReasonDialog.vue';
import { useVoicePrompt } from 'dashboard/components-next/polaris/pageComponents/voice/useVoicePrompt';

const { t } = useI18n();
const route = useRoute();
const store = useStore();

const assistantId = computed(() => Number(route.params.assistantId));
const voice = useVoicePrompt(assistantId);
const { state, drift, versions, auditEvents, flags } = voice;

// Same reading as Settings: below 56rem of its own width the sections go to one column.
const sections = ref(null);
const { width: sectionsWidth } = useElementSize(sections);
const narrow = computed(
  () => sectionsWidth.value > 0 && sectionsWidth.value < 896
);

const emptyForm = () => ({
  productSheet: '',
  process: '',
  connections: '',
  voiceNotes: '',
  brain: 'elevenlabs',
});
const form = ref(emptyForm());
const snapshot = ref(JSON.stringify(emptyForm()));
const dirty = computed(() => JSON.stringify(form.value) !== snapshot.value);
const tooLong = computed(() => {
  const limits = state.value?.limits;
  if (!limits) return false;
  const { productSheet, process, connections, voiceNotes } = form.value;
  return (
    [productSheet, process, connections].some(
      block => block.length > limits.prompt_block
    ) || voiceNotes.length > limits.voice_prompt_extra
  );
});

const fillForm = editable => {
  const blocks = editable?.prompt_blocks || {};
  const voiceConfig = editable?.voice || {};
  form.value = {
    productSheet: blocks.product_sheet || '',
    process: blocks.process || '',
    connections: blocks.connections || '',
    voiceNotes: voiceConfig.voice_prompt_extra || '',
    brain: voiceConfig.brain || 'elevenlabs',
  };
  snapshot.value = JSON.stringify(form.value);
};

const failed = ref(false);
const load = async () => {
  failed.value = false;
  try {
    await voice.load();
    fillForm(state.value.editable);
  } catch {
    failed.value = true;
  }
};
watch(assistantId, load, { immediate: true });

// The other assistant pages save the whole config from the store's copy: it must know what was saved here.
// A failed refresh changes nothing that was saved, so it stays quiet.
const refreshAssistant = () =>
  store.dispatch('polarisAssistants/show', assistantId.value).catch(() => {});

const save = async () => {
  try {
    await voice.save({
      promptBlocks: {
        product_sheet: form.value.productSheet,
        process: form.value.process,
        connections: form.value.connections,
      },
      voice: {
        voice_prompt_extra: form.value.voiceNotes,
        brain: form.value.brain,
      },
    });
    fillForm(state.value.editable);
    refreshAssistant();
    useAlert(t('POLARIS.ASSISTANTS.VOICE.API.SAVE_SUCCESS'));
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        t('POLARIS.ASSISTANTS.VOICE.API.SAVE_ERROR')
    );
  }
};

// A version says how its publication went: the message follows it.
const sayPublished = version => {
  const key = `POLARIS.ASSISTANTS.VOICE.API.PUBLISHED.${version.sync_status.toUpperCase()}`;
  useAlert(t(key, { version: version.version_no }));
};

const publishDialog = ref(null);
const publish = async reason => {
  try {
    sayPublished(await voice.publish(reason));
    publishDialog.value?.close();
  } catch {
    useAlert(t('POLARIS.ASSISTANTS.VOICE.API.PUBLISH_ERROR'));
  }
};

const restoreDialog = ref(null);
const restoring = ref(null);
const askRestore = version => {
  restoring.value = version;
  restoreDialog.value?.open();
};
const restore = async reason => {
  try {
    sayPublished(await voice.restore(restoring.value.version_no, reason));
    fillForm(state.value.editable);
    refreshAssistant();
    restoreDialog.value?.close();
  } catch {
    useAlert(t('POLARIS.ASSISTANTS.VOICE.API.RESTORE_ERROR'));
  }
};

// Leaving with unsaved changes asks first (a route change, or another assistant from the picker), as in Settings.
const leaveDialog = ref(null);
let pendingLeave = null;
const guard = next => {
  if (!dirty.value) {
    next();
    return;
  }
  pendingLeave = next;
  leaveDialog.value?.open();
};
onBeforeRouteLeave((to, from, next) => guard(next));
onBeforeRouteUpdate((to, from, next) => guard(next));
const keepEditing = () => {
  pendingLeave?.(false);
  pendingLeave = null;
};
const discard = () => {
  fillForm(state.value?.editable);
  const next = pendingLeave;
  pendingLeave = null;
  leaveDialog.value?.close();
  next?.();
};
</script>

<template>
  <PageLayout
    preview="folded"
    :header-title="t('POLARIS.ASSISTANTS.VOICE.TITLE')"
    :is-fetching="flags.fetching"
    :show-pagination-footer="false"
    :show-know-more="false"
  >
    <template v-if="state" #headerActions>
      <Button
        variant="outline"
        size="sm"
        :disabled="!dirty || tooLong || flags.saving"
        data-voice-save
        @click="save"
      >
        {{ t('POLARIS.DRAWER.SAVE') }}
      </Button>
      <Button
        size="sm"
        :disabled="dirty || flags.publishing"
        data-voice-publish
        @click="publishDialog?.open()"
      >
        {{ t('POLARIS.ASSISTANTS.VOICE.PUBLISH.ACTION') }}
      </Button>
    </template>
    <template #body>
      <p
        v-if="failed"
        class="type-polaris-body text-polaris-muted-foreground"
        data-voice-load-error
      >
        {{ t('POLARIS.ASSISTANTS.VOICE.API.LOAD_ERROR') }}
      </p>
      <div v-else-if="state" ref="sections" class="grid gap-6">
        <p class="max-w-prose type-polaris-body text-polaris-muted-foreground">
          {{ t('POLARIS.ASSISTANTS.VOICE.DESCRIPTION') }}
        </p>
        <p
          v-if="dirty"
          class="type-polaris-body"
          role="status"
          data-voice-unsaved
        >
          {{ t('POLARIS.ASSISTANTS.VOICE.PUBLISH.SAVE_FIRST') }}
        </p>
        <SettingsSection
          :wide="narrow"
          :title="t('POLARIS.ASSISTANTS.VOICE.STATUS.TITLE')"
          :description="t('POLARIS.ASSISTANTS.VOICE.STATUS.DESCRIPTION')"
        >
          <VoicePublicationStatus
            :targets="state.targets"
            :drift="drift"
            :latest-version="state.latest_version"
            :unversioned-changes="state.unversioned_changes"
            :checking="flags.checkingDrift"
            @recheck="voice.checkDrift"
          />
        </SettingsSection>
        <SettingsSection
          :wide="narrow"
          :title="t('POLARIS.ASSISTANTS.VOICE.INSTRUCTIONS.TITLE')"
          :description="t('POLARIS.ASSISTANTS.VOICE.INSTRUCTIONS.DESCRIPTION')"
        >
          <VoiceInstructionsForm
            v-model="form"
            :limits="state.limits"
            :brains="state.brains"
          />
        </SettingsSection>
        <SettingsSection
          wide
          :title="t('POLARIS.ASSISTANTS.VOICE.COMPILED.TITLE')"
          :description="t('POLARIS.ASSISTANTS.VOICE.COMPILED.DESCRIPTION')"
        >
          <VoiceCompiledPrompt :compiled="state.compiled" />
        </SettingsSection>
        <SettingsSection
          wide
          :title="t('POLARIS.ASSISTANTS.VOICE.HISTORY.TITLE')"
          :description="t('POLARIS.ASSISTANTS.VOICE.HISTORY.DESCRIPTION')"
        >
          <VoiceHistory
            :versions="versions"
            :audit-events="auditEvents"
            :busy="flags.publishing"
            @restore="askRestore"
            @versions-page="voice.loadVersions"
            @audit-events-page="voice.loadAuditEvents"
          />
        </SettingsSection>
      </div>
    </template>
    <VoiceReasonDialog
      ref="publishDialog"
      :title="t('POLARIS.ASSISTANTS.VOICE.PUBLISH.TITLE')"
      :description="t('POLARIS.ASSISTANTS.VOICE.PUBLISH.DESCRIPTION')"
      :confirm-label="t('POLARIS.ASSISTANTS.VOICE.PUBLISH.ACTION')"
      :is-loading="flags.publishing"
      @confirm="publish"
    />
    <VoiceReasonDialog
      ref="restoreDialog"
      :title="
        t('POLARIS.ASSISTANTS.VOICE.RESTORE.TITLE', {
          version: restoring?.version_no,
        })
      "
      :description="t('POLARIS.ASSISTANTS.VOICE.RESTORE.DESCRIPTION')"
      :confirm-label="t('POLARIS.ASSISTANTS.VOICE.HISTORY.RESTORE')"
      :is-loading="flags.publishing"
      @confirm="restore"
    />
    <Dialog
      ref="leaveDialog"
      type="alert"
      width="md"
      :title="t('POLARIS.DRAWER.DISCARD_TITLE')"
      :description="t('POLARIS.DRAWER.DISCARD_TEXT')"
      :cancel-button-label="t('POLARIS.DRAWER.KEEP_EDITING')"
      :confirm-button-label="t('POLARIS.DRAWER.DISCARD')"
      @confirm="discard"
      @close="keepEditing"
    />
  </PageLayout>
</template>
